import './styles.sass';

import React from 'react';
import { cloneDeep } from 'lodash-es';

import PageTitle from 'components/Explorer/PageTitle';
import * as QueryParams from 'components/Explorer/models/query_params';
import useExplorerData, { Loading } from 'hooks/useExplorerData';
import useQueryParams from 'hooks/useQueryParams';
import useNavigateParams from 'hooks/useNavigateParams';

import Tabs from './Tabs';
import Summary from './Summary';
import Map from './Map';
import Table from './Table';

const Demographics = () => {
  const params = useQueryParams();
  const navigateParams = useNavigateParams();

  const { data, loading } = useExplorerData('demographics', params, { reloadOn: ['filters'] });

  const selectedSource = params.filters.filter || Tabs.TYPES[0];
  const isLoading = loading === Loading.NOT_STARTED || loading === Loading.IN_PROGRESS;
  const loadingFailed = loading === Loading.ERROR;

  const setSource = (filter) => {
    const newFilters = cloneDeep(params.filters);
    if (filter === Tabs.TYPES[0]) {
      delete newFilters.filter;
    } else {
      newFilters.filter = filter;
    }
    Analytics.trackEvent('demographics-set-source', { filter });
    navigateParams(QueryParams.withFilters(params, newFilters), { replace: true });
  };

  const filtersForMentions = (countryCode, source) => {
    const newFilters = cloneDeep(params.filters);
    newFilters['countries[]'] = countryCode;
    newFilters['mention_sources[]'] = `type:${source}`;
    return QueryParams.withFilters(params, newFilters);
  };

  const locationForFilter = (countryCode, source) =>
    QueryParams.locationFor(filtersForMentions(countryCode, source), '/mentions');

  const openMentions = (countryCode, source) => {
    Analytics.trackEvent('demographics-click-map', { country: countryCode, source });
    navigateParams(filtersForMentions(countryCode, source), { pathname: '/mentions', replace: true });
  };

  const renderContent = () => {
    if (loadingFailed) {
      return <div className="Explorer-Demographics-empty-message">{I18n.t('Explorer.loading_error')}</div>;
    }
    if (!data?.countries?.length) {
      return <div className="Explorer-Demographics-empty-message">{I18n.t('Explorer.Demographics.empty')}</div>;
    }
    return (
      <div className="Explorer-Demographics-content">
        <a
          href={I18n.t('Explorer.help.demographics.uri')}
          target="_blank"
          rel="noreferrer"
          title={I18n.t('Explorer.help.demographics.content')}
          className="help"
        >
          {I18n.t('Explorer.help.label_tab')}
        </a>
        <Summary data={data.total} selectedSource={selectedSource} />
        <div className="Explorer-Demographics-content-data">
          <Map
            data={data.countries}
            totals={data.total}
            selectedSource={selectedSource}
            onViewMentions={openMentions}
            queryDescription={data.query_description}
          />
          <Table data={data.countries} selectedSource={selectedSource} locationBuilder={locationForFilter} />
        </div>
      </div>
    );
  };

  return (
    <div className="Explorer-Demographics">
      <PageTitle metaTitle={I18n.t('Explorer.TabBar.demographics')} />
      <Tabs selectedSource={selectedSource} onChange={setSource} />
      <div className="Explorer-Demographics-Container">
        {isLoading ? (
          <div className="Explorer-Demographics-loading-message">{I18n.t('Explorer.Demographics.loading')}</div>
        ) : (
          renderContent()
        )}
      </div>
    </div>
  );
};

export default Demographics;
